// Decisions behind scanning a container code in the packing input (CUBBO-2689).

// Wider than the backend's own resolution (`product_codes.sku` or
// `products.bar_code`) on purpose: this is a safety gate, so every code shape the
// rest of the file matches on counts as owned by the order. Matching too much
// sends a scan to the dialog; matching too little packs a piece behind the
// operator's back.
const matchesProduct = (product, code) =>
  !!product && (product.bar_code === code || (product.product_codes || []).some(pc => pc.sku === code || pc.upc === code || pc.bar_code === code))

// Whether the code belongs to this order in either form `/packing-boxes/:id/scan`
// resolves: a picked unit's own code, or a product code (the line's product or a
// kit child). Deliberately wider than the component's product matcher, which only
// sees reviewable products — non-scannable lines, dropshipping kit children and
// already-complete lines are missing from it, and a unit code is not a product
// code at all.
//
// Only a code that matches nothing here may be tried as a container. Otherwise the
// backend packs the piece and the unit-code capture is skipped, because nothing
// server-side requires it. IMEI capture is not at risk — that one is enforced
// inside the same transaction and rolls the scan back.
export const orderContainsCode = (orderLines, code) => {
  // Without this an absent code matches every absent one on the order.
  if (!code) return false
  // No lines to check means we cannot clear the code, and the gate fails closed.
  if (!orderLines) return true
  return orderLines.some(line => {
    const product = line?.product
    if (matchesProduct(product, code)) return true
    if ((product?.kit_childs || []).some(kitChild => matchesProduct(kitChild?.child_product, code))) return true
    return (line?.stock_units || []).some(stockUnit => stockUnit?.unit_code === code)
  })
}

// Where a scan lands: the box the operator is on, or the first one still open.
// Mirrors what piece scans have always done, so both paths agree on the target.
export const pickTargetBox = (boxes, activeBoxId) => {
  const open = (boxes || []).filter(box => box && box.status !== 'closed')
  return open.find(box => box.id === activeBoxId) || open[0] || null
}

// The codes that mean the backend read the scan as a piece, not a container:
// either it matched nothing, or it named a unit that cannot go in this order.
// That is the same mis-scan the warning dialog covered before containers
// existed, and it stays blocking. Container rejections say something specific
// about the contents instead, and belong in the error bar with their own message.
const PIECE_LEVEL_CODES = ['unit_not_found', 'unit_not_pickable', 'unit_already_packed']

export const isNotAContainer = errorCode => PIECE_LEVEL_CODES.includes(errorCode)

// The product a scanned unit code counts as: a pending unit-code product with a line
// that already holds that unit. A piece whose only barcode is its unit code (a
// personalized card) is identified by it alone, so it needs neither its product code
// nor the unit-code dialog. Only a unit picked for the order passes, as in the dialog
// (which looks at the product's first line only; this looks at all of them). A unit
// already captured is not a second piece. IMEI products keep their own capture.
export const productForUnitCode = (products, orderLines, code, capturedUnitCodes) => {
  if (!code || !orderLines || capturedUnitCodes.includes(code)) return undefined
  return products.find(
    product =>
      product.requires_unit_code &&
      !product.has_imei &&
      (product.reviewedItemsCount || 0) < product.quantity &&
      orderLines.some(line => line?.product?.id === product.id && (line.stock_units || []).some(stockUnit => stockUnit?.unit_code === code)),
  )
}

// productsData without one captured unit code: the capture of a piece that did not
// land (no open box, a refused scan, an unscan) is undone so the card can be scanned
// again. A code that was never captured leaves it as it is.
export const withoutUnitCode = (productsData, productId, code) =>
  (productsData || []).map(pd => (pd.product_id === productId ? { ...pd, unit_codes: (pd.unit_codes || []).filter(c => c !== code) } : pd))
